/* ==========================================================================
   Plåtwebb Kalkylator — V1.4
   Ändringar från V1.3:
     - Dynamic-variant-flöde via Supabase Edge Function (Fas 5)
     - SHA-256-hash av normaliserad payload (matchar server byte-för-byte)
     - Server-side prisrekonstruktion = single source of truth
     - Bakåtkomp: utan PLATWEBB_EDGE_FN_URL körs gammal direkt-add-flow
   ========================================================================== */
(function () {
  'use strict';

  var qs  = function(sel, root) { return (root || document).querySelector(sel); };
  var qsa = function(sel, root) { return Array.from((root || document).querySelectorAll(sel)); };

  var mkSvg = function(tag, attrs) {
    var e = document.createElementNS('http://www.w3.org/2000/svg', tag);
    attrs = attrs || {};
    Object.keys(attrs).forEach(function(k) { e.setAttribute(k, attrs[k]); });
    return e;
  };

  var formatSek = function(n) {
    return isNaN(n) ? '\u2014' : Math.round(n).toLocaleString('sv-SE');
  };

  // ---- Prisberäkning -------------------------------------------------------

  function findTier(config, qty) {
    var tiers = (config.quantity_discount && config.quantity_discount.tiers) || [];
    var hit = null;
    tiers.forEach(function(t) {
      if (qty >= t.min_qty && (t.max_qty === null || qty <= t.max_qty)) hit = t;
    });
    return hit || { discount_pct: 0 };
  }

  // V1.5 (2026-05-03): rabatt-feature-flag. Sätts från Liquid via
  // data-pw-discount-enabled på root-elementet. Default false → ingen
  // staffelrabatt syns eller räknas. Plåtwebb kan slå på det igen senare.
  var PW_VOLUME_DISCOUNT_ENABLED = false;
  function calcUnitPrice(config, values, qty) {
    var p      = config.pricing;
    var length = Number(values.length) || p.length_reference_mm;
    var base   = (p.base_price_sek || 0) * (length / (p.length_reference_mm || 1000));

    var mat    = null;
    config.materials.forEach(function(m) { if (m.id === values._material) mat = m; });
    if (!mat) mat = config.materials[0];
    var matSur = (mat && mat.surcharge_sek) || 0;

    var colSur = 0;
    if (values._color_ral === '_extended') {
      colSur = (config.extended_ral && config.extended_ral.surcharge_sek) || 0;
    } else {
      var col = null;
      config.colors.forEach(function(c) { if (c.ral === values._color_ral) col = c; });
      colSur = (col && col.surcharge_sek) || 0;
    }

    var gross = base + matSur + colSur;
    var disc  = PW_VOLUME_DISCOUNT_ENABLED ? (findTier(config, qty).discount_pct || 0) : 0;
    var unit  = gross * (1 - disc / 100);
    var vat   = p.vat_included ? 1 : 1 + (p.vat_rate || 0);

    return {
      unit_ex:      Math.round(unit * 100) / 100,
      unit_inc:     Math.round(unit * vat * 100) / 100,
      total_ex:     Math.round(unit * qty * 100) / 100,
      total_inc:    Math.round(unit * vat * qty * 100) / 100,
      unit_ore:     Math.round(unit * vat * 100),         // öre, inkl. moms — matchar server
      total_ore:    Math.round(unit * vat * qty * 100),   // öre, inkl. moms — matchar server
      discount_pct: disc,
      qty:          qty
    };
  }

  // ---- Geometrimotor (V1.1 — 2026-05-01) ---------------------------------
  //
  // Turtle-graphics i SVG-koordinater (y-ned, x-höger).
  //   - Defaultstart `dir = -90` -> första segmentet pekar UPP på skärmen.
  //   - Varje `rotation_after` vrider riktningen MEDURS i SVG med `angle` grader.
  //       90 grader  = rätvinklig bockning
  //       <90        = skarpare (bockas längre)
  //       >90        = mjukare
  //   Denna konvention matchar CAD-ritningarnas vinkelvärden direkt och
  //   motsvarar 3D-motorn i `plat-kalkylator-3d.js`.

  function buildPath(config, values) {
    var segs = config.geometry.segments;
    var startDir = (config.geometry.start_direction != null)
      ? Number(config.geometry.start_direction)
      : -90;
    var x = 0, y = 0, dir = startDir;
    var pts = [{ x: 0, y: 0 }];

    segs.forEach(function(seg) {
      var len = Number(values[seg.dimension]) || 1;
      x += len * Math.cos(dir * Math.PI / 180);
      y += len * Math.sin(dir * Math.PI / 180);
      pts.push({ x: x, y: y, len: len, label: seg.dimension.toUpperCase() });
      if (seg.rotation_after) {
        var rot = Number(values[seg.rotation_after]) || 90;
        // V1.1 (2026-05-01): stöd för rotation_convention="inner_angle".
        // Default = "bend" (cursor vrids med rot grader vid hörnet — så som
        // sockelplåt och fönsterbleck konfigurerats). För profiler där
        // rotation_after representerar INSIDESVINKELN (t.ex. taknock med
        // Nockvinkel=130°), sätt geometry.rotation_convention="inner_angle"
        // så vrids cursor istället med (180 - rot) grader.
        if (config.geometry.rotation_convention === 'inner_angle') {
          rot = 180 - rot;
        }
        dir += rot;
      }
    });

    var xs = pts.map(function(p) { return p.x; });
    var ys = pts.map(function(p) { return p.y; });
    var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
    return {
      pts: pts,
      bbox: { minX: minX, maxX: maxX, minY: minY, maxY: maxY,
              w: maxX - minX, h: maxY - minY }
    };
  }

  // ---- SVG rendering -------------------------------------------------------
  //
  // Isometrisk antracit-rendering: vi extruderar tvärsnittet till en
  // "ribbon" i isometrisk 2D och skuggar varje segment beroende på dess
  // gåriktning relativt en ljuskälla i övre-höger. Visuellt en produktbild,
  // INTE en teknisk ritning - men fortfarande ren SVG (ingen WebGL, inga
  // transforms, ingen rotation). Basfärgen kommer från kundens RAL-val
  // via `values._color_hex`.

  function shade(hex, amt) {
    var h = (hex || '#474646').replace('#', '');
    var r = parseInt(h.slice(0,2) || '47', 16);
    var g = parseInt(h.slice(2,4) || '46', 16);
    var b = parseInt(h.slice(4,6) || '46', 16);
    var clamp = function(v) { return Math.max(0, Math.min(255, v)); };
    return '#' + [r,g,b].map(function(v) {
      return clamp(v+amt).toString(16).padStart(2,'0');
    }).join('');
  }

  // Mappa [0..1] brightness till hex-ton baserat på bas-färgen.
  // brightness 0.5 ~ bas, <0.5 mörkare, >0.5 ljusare.
  function tone(baseHex, brightness) {
    var b = Math.max(0, Math.min(1, brightness));
    var amt = Math.round((b - 0.5) * 140);
    return shade(baseHex, amt);
  }

  // Enhetsvektor mot ljuskällan i SVG-koordinater (y-ned). Övre-höger.
  var LIGHT = { x: 0.78, y: -0.62 };

  function faceBrightness(p0, p1) {
    var wx = p1[0] - p0[0], wy = p1[1] - p0[1];
    var wl = Math.hypot(wx, wy) || 1;
    var nx = wy / wl;   // CCW 90 grader av gåriktningen i SVG y-ned
    var ny = -wx / wl;
    var dot = nx * LIGHT.x + ny * LIGHT.y;
    return Math.max(0.08, Math.min(0.94, 0.50 + dot * 0.55));
  }

  function renderSvg(svgRoot, config, values) {
    svgRoot.innerHTML = '';

    var result = buildPath(config, values);
    var pts    = result.pts;
    var bbox   = result.bbox;

    var rect = svgRoot.getBoundingClientRect();
    var W = rect.width  > 20 ? rect.width  : 420;
    var H = rect.height > 20 ? rect.height : 280;

    // Skala tvärsnittet så det fyller canvasen med luft runt.
    var PAD   = 64;
    var bboxW = Math.max(bbox.w, 1);
    var bboxH = Math.max(bbox.h, 1);

    // V1.4 (2026-05-03 sen): SIDOVY MED LÄTT LUTNING. V1.3 hade ISO_X=0.25,
    // ISO_Y=-0.15 vilket gav ~31° projektionsvinkel — för bratt så att en
    // 2000×250 ståndskiva fortfarande lästes som ~1:1 i viewporten.
    //
    // Nu använder vi en nästan ren sidovy (~6° lutning) så att L-axeln
    // dominerar horisontellt och produktens faktiska aspect ratio går
    // direkt fram. En 2000×250 ståndskiva får ~4:1+ visuell aspect, en
    // 250×250-kub får ~1:1, och en 100×3000 vindskiveplåt blir tydligt
    // avlång — exakt som katalogfotona.
    var length  = Number(values.length) || 1000;
    var depthMm = Math.max(70, length);   // floor 70 mm så pyttekorta produkter syns
    var ISO_X   = 0.95;    // sidovy: depth-axeln nästan helt horisontell
    var ISO_Y   = -0.10;   // ~6° lutning uppåt så vi behåller lite 3D-känsla

    // Inkludera djup-projektionen i bbox så scale tar hänsyn till hela scenen.
    var fullW = bboxW + ISO_X * depthMm;
    var fullH = bboxH + Math.abs(ISO_Y) * depthMm;
    var scale = Math.min((W - PAD*2) / fullW, (H - PAD*2) / fullH) * 0.70;

    var toSvg = function(wx, wy) {
      return [wx * scale, wy * scale];
    };

    var ddx = ISO_X * depthMm * scale;
    var ddy = ISO_Y * depthMm * scale;

    var nearPts = pts.map(function(p) { return toSvg(p.x, p.y); });
    var farPts  = nearPts.map(function(pt) { return [pt[0] + ddx, pt[1] + ddy]; });

    // Auto-viewBox över hela scenen (near + far + marginaler).
    var allPts = nearPts.concat(farPts);
    var xs = allPts.map(function(p) { return p[0]; });
    var ys = allPts.map(function(p) { return p[1]; });
    var minX = Math.min.apply(null, xs), maxX = Math.max.apply(null, xs);
    var minY = Math.min.apply(null, ys), maxY = Math.max.apply(null, ys);
    var marginX = 48;
    var marginTop = 56;
    var marginBottom = 70;
    var vbX = minX - marginX;
    var vbY = minY - marginTop;
    var vbW = (maxX - minX) + marginX * 2;
    var vbH = (maxY - minY) + marginTop + marginBottom;
    svgRoot.setAttribute('viewBox', vbX.toFixed(1) + ' ' + vbY.toFixed(1) + ' ' +
                                     vbW.toFixed(1) + ' ' + vbH.toFixed(1));

    var strokeScale = Math.max(vbW, vbH) / 560;

    var baseColor = values._color_hex || '#474646';
    var annCol    = '#9aa6b8';

    var g = mkSvg('g');

    // 1) Slagskugga-definition (Gaussian blur-filter)
    var shadowId = 'pwShadow' + Math.random().toString(36).slice(2,7);
    var defs = mkSvg('defs');
    var filter = mkSvg('filter', { id: shadowId,
      x:'-20%', y:'-20%', width:'140%', height:'140%' });
    filter.appendChild(mkSvg('feGaussianBlur', { stdDeviation: (5 * strokeScale).toFixed(1) }));
    defs.appendChild(filter);

    // 2) Per-face-gradienter baserat på brightness per segment
    var faceGrads = [];
    for (var fi = 0; fi < nearPts.length - 1; fi++) {
      var b = faceBrightness(nearPts[fi], nearPts[fi + 1]);
      var gid = 'pwFG' + fi + Math.random().toString(36).slice(2,5);
      var grad = mkSvg('linearGradient', {
        id: gid,
        gradientUnits: 'userSpaceOnUse',
        x1: nearPts[fi][0].toFixed(1),   y1: nearPts[fi][1].toFixed(1),
        x2: farPts[fi][0].toFixed(1),    y2: farPts[fi][1].toFixed(1)
      });
      grad.appendChild(mkSvg('stop', { offset: '0%',   'stop-color': tone(baseColor, b + 0.06) }));
      grad.appendChild(mkSvg('stop', { offset: '55%',  'stop-color': tone(baseColor, b) }));
      grad.appendChild(mkSvg('stop', { offset: '100%', 'stop-color': tone(baseColor, b - 0.07) }));
      defs.appendChild(grad);
      faceGrads.push({ id: gid, brightness: b });
    }
    g.appendChild(defs);

    // 3) Slagskugga under blecket
    var shCX = (minX + maxX) / 2;
    var shCY = maxY + 22 * strokeScale;
    var shRX = (maxX - minX) * 0.52;
    var shRY = Math.max(6, shRX * 0.08);
    g.appendChild(mkSvg('ellipse', {
      cx: shCX.toFixed(1), cy: shCY.toFixed(1),
      rx: shRX.toFixed(1), ry: shRY.toFixed(1),
      fill: 'rgba(0,0,0,0.55)',
      filter: 'url(#' + shadowId + ')'
    }));

    // 4) Segment-ytor: parallellogrammer near[i]-near[i+1]-far[i+1]-far[i]
    for (var i = 0; i < nearPts.length - 1; i++) {
      var quad = [nearPts[i], nearPts[i+1], farPts[i+1], farPts[i]];
      var points = quad.map(function(pt) { return pt[0].toFixed(1) + ',' + pt[1].toFixed(1); }).join(' ');
      g.appendChild(mkSvg('polygon', {
        points: points,
        fill: 'url(#' + faceGrads[i].id + ')',
        stroke: 'rgba(0,0,0,0.4)',
        'stroke-width': (0.5 * strokeScale).toFixed(2),
        'stroke-linejoin': 'miter',
        'shape-rendering': 'geometricPrecision'
      }));
    }

    // 5) Bakre kontur (svag - ger djup)
    var farStr = farPts.map(function(pt, i) {
      return (i ? 'L' : 'M') + pt[0].toFixed(1) + ',' + pt[1].toFixed(1);
    }).join(' ');
    g.appendChild(mkSvg('path', {
      d: farStr, fill: 'none',
      stroke: 'rgba(0,0,0,0.35)',
      'stroke-width': (0.7 * strokeScale).toFixed(2),
      'stroke-linejoin': 'miter',
      'stroke-linecap': 'butt'
    }));

    // 6) Främre kontur (mörk plåtkant)
    var nearStr = nearPts.map(function(pt, i) {
      return (i ? 'L' : 'M') + pt[0].toFixed(1) + ',' + pt[1].toFixed(1);
    }).join(' ');
    g.appendChild(mkSvg('path', {
      d: nearStr, fill: 'none',
      stroke: shade(baseColor, -55),
      'stroke-width': (1.4 * strokeScale).toFixed(2),
      'stroke-linejoin': 'miter',
      'stroke-linecap': 'butt'
    }));

    // 7) Specular highlight på det ljusaste segmentet
    if (faceGrads.length > 0) {
      var maxBIdx = 0;
      for (var bi = 1; bi < faceGrads.length; bi++) {
        if (faceGrads[bi].brightness > faceGrads[maxBIdx].brightness) maxBIdx = bi;
      }
      var mix = function(n, f, t) { return [n[0]*(1-t) + f[0]*t, n[1]*(1-t) + f[1]*t]; };
      var h1 = mix(nearPts[maxBIdx],   farPts[maxBIdx],   0.18);
      var h2 = mix(nearPts[maxBIdx+1], farPts[maxBIdx+1], 0.18);
      g.appendChild(mkSvg('line', {
        x1: h1[0].toFixed(1), y1: h1[1].toFixed(1),
        x2: h2[0].toFixed(1), y2: h2[1].toFixed(1),
        stroke: 'rgba(255,255,255,0.18)',
        'stroke-width': (0.9 * strokeScale).toFixed(2),
        'stroke-linecap': 'round'
      }));
    }

    // 8) MÅTTLINJER PER JUSTERBAR DIMENSION (V1.4 — 2026-05-03)
    //    En måttlinje per segment (A, B, kantflik, drop osv) längs den
    //    faktiska kanten + en L-måttlinje längs depth-axeln. Mörk färg
    //    för synlighet. Linjen ligger PARALLELL med kanten, offsetad
    //    utåt från path-centroiden, med ticks vid endpoints.
    var dimColor = '#474646';                 // pw-brand-dark — tydlig kontrast mot warm bg
    var dimTickColor = 'rgba(71,70,70,0.6)';  // tick-marks lite svagare
    var dimSw = (1.0 * strokeScale).toFixed(2);
    var dimFont = (11.5 * strokeScale).toFixed(1);
    var dimOffset = 24 * strokeScale;         // hur långt utåt linjen läggs
    var tickLen = 8 * strokeScale;
    var arrowLen = 6 * strokeScale;

    // Centroid av near-pts för att avgöra "utåt" sida per segment
    var cx0 = 0, cy0 = 0;
    nearPts.forEach(function(p) { cx0 += p[0]; cy0 += p[1]; });
    cx0 /= nearPts.length; cy0 /= nearPts.length;

    function drawDimension(p1, p2, label, valueMm, options) {
      options = options || {};
      var dx = p2[0] - p1[0];
      var dy = p2[1] - p1[1];
      var segLen = Math.sqrt(dx*dx + dy*dy);
      if (segLen < 2) return;  // ignorera mikro-segment

      var ux = dx / segLen, uy = dy / segLen;
      // Två kandidater för perpendikulär riktning
      var n1x = -uy, n1y = ux;     // 90° CCW
      var n2x =  uy, n2y = -ux;    // 90° CW
      // Välj den som pekar BORT från centroiden (= utåt)
      var midX = (p1[0] + p2[0]) / 2;
      var midY = (p1[1] + p2[1]) / 2;
      var toCx = cx0 - midX, toCy = cy0 - midY;
      var dot1 = n1x * toCx + n1y * toCy;
      var nx, ny;
      if (dot1 < 0) { nx = n1x; ny = n1y; }
      else { nx = n2x; ny = n2y; }

      // Tillåt force-side via options.side ('cw' / 'ccw')
      if (options.side === 'cw') { nx = n2x; ny = n2y; }
      if (options.side === 'ccw') { nx = n1x; ny = n1y; }

      var off = options.offset || dimOffset;
      var dpx1 = p1[0] + nx * off;
      var dpy1 = p1[1] + ny * off;
      var dpx2 = p2[0] + nx * off;
      var dpy2 = p2[1] + ny * off;

      // Tick-linjer från segment-endpoint ut till måttlinje
      g.appendChild(mkSvg('line', {
        x1: p1[0].toFixed(1), y1: p1[1].toFixed(1),
        x2: dpx1.toFixed(1),  y2: dpy1.toFixed(1),
        stroke: dimTickColor, 'stroke-width': dimSw,
        'stroke-dasharray': (3 * strokeScale).toFixed(1) + ' ' + (3 * strokeScale).toFixed(1)
      }));
      g.appendChild(mkSvg('line', {
        x1: p2[0].toFixed(1), y1: p2[1].toFixed(1),
        x2: dpx2.toFixed(1),  y2: dpy2.toFixed(1),
        stroke: dimTickColor, 'stroke-width': dimSw,
        'stroke-dasharray': (3 * strokeScale).toFixed(1) + ' ' + (3 * strokeScale).toFixed(1)
      }));
      // Huvudmåttlinje
      g.appendChild(mkSvg('line', {
        x1: dpx1.toFixed(1), y1: dpy1.toFixed(1),
        x2: dpx2.toFixed(1), y2: dpy2.toFixed(1),
        stroke: dimColor, 'stroke-width': dimSw
      }));
      // Pilspetsar in mot endpoints
      var arrUx = ux * arrowLen, arrUy = uy * arrowLen;
      var arrNx = nx * (arrowLen * 0.5), arrNy = ny * (arrowLen * 0.5);
      // Vänster pil
      g.appendChild(mkSvg('line', {
        x1: dpx1.toFixed(1), y1: dpy1.toFixed(1),
        x2: (dpx1 + arrUx + arrNx).toFixed(1), y2: (dpy1 + arrUy + arrNy).toFixed(1),
        stroke: dimColor, 'stroke-width': dimSw, 'stroke-linecap': 'round'
      }));
      g.appendChild(mkSvg('line', {
        x1: dpx1.toFixed(1), y1: dpy1.toFixed(1),
        x2: (dpx1 + arrUx - arrNx).toFixed(1), y2: (dpy1 + arrUy - arrNy).toFixed(1),
        stroke: dimColor, 'stroke-width': dimSw, 'stroke-linecap': 'round'
      }));
      // Höger pil
      g.appendChild(mkSvg('line', {
        x1: dpx2.toFixed(1), y1: dpy2.toFixed(1),
        x2: (dpx2 - arrUx + arrNx).toFixed(1), y2: (dpy2 - arrUy + arrNy).toFixed(1),
        stroke: dimColor, 'stroke-width': dimSw, 'stroke-linecap': 'round'
      }));
      g.appendChild(mkSvg('line', {
        x1: dpx2.toFixed(1), y1: dpy2.toFixed(1),
        x2: (dpx2 - arrUx - arrNx).toFixed(1), y2: (dpy2 - arrUy - arrNy).toFixed(1),
        stroke: dimColor, 'stroke-width': dimSw, 'stroke-linecap': 'round'
      }));

      // Text-label vid mid-point, en aning offsetad ytterligare utåt
      var labelOff = off + 14 * strokeScale;
      var lmx = midX + nx * labelOff;
      var lmy = midY + ny * labelOff;
      var labelTxt = label + ' ' + Math.round(valueMm) + ' mm';
      // Subtil "wash" bakom texten för att skydda mot bakgrundsskugga
      var charPx = 6.5 * strokeScale;
      var labelW = labelTxt.length * charPx + 12 * strokeScale;
      var labelH = 16 * strokeScale;
      g.appendChild(mkSvg('rect', {
        x: (lmx - labelW/2).toFixed(1),
        y: (lmy - labelH/2 - 1).toFixed(1),
        width: labelW.toFixed(1), height: labelH.toFixed(1),
        rx: (3 * strokeScale).toFixed(1),
        fill: 'rgba(250,248,245,0.92)'
      }));
      var t = mkSvg('text', {
        x: lmx.toFixed(1),
        y: (lmy + 4 * strokeScale).toFixed(1),
        'font-size': dimFont,
        'font-family': 'Poppins,system-ui,sans-serif',
        'font-weight': '600',
        fill: dimColor,
        'text-anchor': 'middle',
        'letter-spacing': '0.3'
      });
      t.textContent = labelTxt;
      g.appendChild(t);
    }

    // 8a) Cross-section-segment per justerbar dim
    var dimDefs = (config.dimensions || []).reduce(function(acc, d) {
      acc[d.id] = d; return acc;
    }, {});
    for (var si = 0; si < pts.length - 1; si++) {
      var p2info = pts[si + 1];
      var dimId = (p2info.label || '').toLowerCase();
      var def = dimDefs[dimId];
      // Hoppa över om dim är fixed (admin-styrt) eller om len < 8 mm (visuell brus)
      if (def && def.fixed) continue;
      if ((p2info.len || 0) < 8) continue;
      var labelTxt = (p2info.label || dimId.toUpperCase());
      drawDimension(nearPts[si], nearPts[si + 1], labelTxt, p2info.len || 0);
    }

    // 8b) L-måttlinje längs depth-axeln (välj den nedre kanten — minst clutter)
    var lval = Math.round(Number(values.length) || 0);
    if (lval > 0 && nearPts.length >= 1) {
      // Hitta den punkt som är längst NER (max y) — där lägger vi L-linjen
      var botIdx = 0;
      for (var ti = 1; ti < nearPts.length; ti++) {
        if (nearPts[ti][1] > nearPts[botIdx][1]) botIdx = ti;
      }
      drawDimension(nearPts[botIdx], farPts[botIdx], 'L', lval, { offset: 28 * strokeScale });
    }

    // 9) Proportions-caption (V1.3): liten chip nere till vänster som visar
    //    de faktiska måtten i klartext, så kunden alltid kan verifiera att
    //    bilden stämmer med deras input. "Vy: 250 × 250 mm".
    var captionMm = '';
    if (lval > 0) {
      // Cross-section "höjd" = bbox huvuddimension (det A-måttet kunden
      // intuitivt jämför mot L). Vi tar den största av bbox.w och bbox.h
      // som "tvärsnittsmått" eftersom det är den dominerande dimensionen.
      var crossMm = Math.round(Math.max(bbox.w || 0, bbox.h || 0));
      if (crossMm > 0) {
        captionMm = 'Vy: ' + crossMm + ' × ' + lval + ' mm';
      } else {
        captionMm = 'Vy: ' + lval + ' mm längd';
      }
    }
    if (captionMm) {
      var capPadX = 10 * strokeScale;
      var capPadY = 6 * strokeScale;
      var capH = 22 * strokeScale;
      var capW = (captionMm.length * 6.5 + 18) * strokeScale;
      var capX = vbX + 14 * strokeScale;
      var capY = vbY + vbH - capH - 14 * strokeScale;

      g.appendChild(mkSvg('rect', {
        x: capX.toFixed(1), y: capY.toFixed(1),
        width: capW.toFixed(1), height: capH.toFixed(1),
        rx: (4 * strokeScale).toFixed(1),
        fill: 'rgba(71,70,70,0.78)'
      }));
      var capText = mkSvg('text', {
        x: (capX + capPadX).toFixed(1),
        y: (capY + capH/2 + 4 * strokeScale).toFixed(1),
        'font-size': (10.5 * strokeScale).toFixed(1),
        'font-family': 'Poppins,system-ui,sans-serif',
        'font-weight': '500',
        fill: '#FAF8F5',
        'letter-spacing': '0.4'
      });
      capText.textContent = captionMm;
      g.appendChild(capText);
    }

    svgRoot.appendChild(g);
  }

  // ---- Edge Function-payload (måste matcha server byte-för-byte) ----------
  //
  // Server normaliserar med exakt samma fält & ordning i hash.ts. Om denna
  // funktion driftar isär från servern → INVALID_HASH 400 vid varje request.
  // Ändra ALDRIG fältnamn, ordning eller villkor utan att uppdatera hash.ts samtidigt.

  function buildVariantPayload(config, values, brQ) {
    // Bygg dimensions-objekt (alla numeriska dim-fält)
    var dimensions = {};
    config.dimensions.forEach(function(d) {
      var v = values[d.id];
      if (v != null) dimensions[d.id] = Number(v);
    });

    // Vinklar
    var angles = {};
    (config.angles || []).forEach(function(a) {
      var v = values[a.id];
      if (v != null) angles[a.id] = Number(v);
    });

    var isExtended = values._color_ral === '_extended';

    return {
      brand_id: window.PLATWEBB_BRAND_ID,
      profile: config.profile_id,
      shopify_product_id: window.PLATWEBB_SHOPIFY_PRODUCT_GID,
      dimensions: dimensions,
      angles: angles,
      material_id: values._material,
      color: {
        ral: isExtended ? null : values._color_ral,
        is_extended: isExtended
      },
      quantity: Number(brQ.qty),
      client_unit_price_ore: brQ.unit_ore,
      client_total_price_ore: brQ.total_ore
    };
  }

  // Kanonisk JSON identisk med server. Sortera dim/angle-nycklar.
  // OBS: client_hash, client_unit_price_ore, client_total_price_ore exkluderade.
  function normalizePayload(req) {
    var dimKeys = Object.keys(req.dimensions).sort();
    var dims = {};
    dimKeys.forEach(function(k) { dims[k] = req.dimensions[k]; });

    var angKeys = Object.keys(req.angles || {}).sort();
    var angs = {};
    angKeys.forEach(function(k) { angs[k] = req.angles[k]; });

    var canonical = {
      brand_id: req.brand_id,
      profile: req.profile,
      shopify_product_id: req.shopify_product_id,
      dimensions: dims,
      angles: angs,
      material_id: req.material_id,
      color: {
        ral: req.color.ral || null,
        is_extended: req.color.is_extended === true
      },
      quantity: req.quantity
    };

    return JSON.stringify(canonical);
  }

  // SHA-256 → hex (matchar Deno's crypto.subtle.digest)
  function sha256Hex(str) {
    var buf = new TextEncoder().encode(str);
    return crypto.subtle.digest('SHA-256', buf).then(function(h) {
      return Array.from(new Uint8Array(h))
        .map(function(b) { return b.toString(16).padStart(2,'0'); })
        .join('');
    });
  }

  // Posta till Edge Function. Returnerar promise med {variant_id, properties, reused}.
  function fetchDynamicVariant(payload) {
    var url = window.PLATWEBB_EDGE_FN_URL;
    if (!url) {
      return Promise.reject(new Error('PLATWEBB_EDGE_FN_URL saknas — dynamic variants ej konfigurerat'));
    }

    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(payload)
    }).then(function(res) {
      return res.json().then(function(body) {
        if (!res.ok || body.ok !== true) {
          var err = new Error(body.message || 'Edge Function-fel');
          err.code = body.error_code || 'UNKNOWN';
          err.status = res.status;
          throw err;
        }
        return body;
      });
    });
  }

  // ---- UI-byggare ----------------------------------------------------------

  function buildUI(root, config) {
    var values = {};

    config.dimensions.forEach(function(d) { values[d.id] = d.default; });
    config.angles.forEach(function(a) { values[a.id] = a.default; });
    var defMat = null;
    config.materials.forEach(function(m) { if (m.is_default) defMat = m; });
    if (!defMat) defMat = config.materials[0];
    values._material  = defMat.id;
    values._color_ral = config.colors[0].ral;
    values._color_hex = config.colors[0].hex;
    values._quantity  = config.quantity_default || 1;

    // Läs feature-flag från Liquid (root element data-attribute)
    var _vdAttr = root.getAttribute('data-pw-discount-enabled');
    PW_VOLUME_DISCOUNT_ENABLED = (_vdAttr === 'true' || _vdAttr === '1');

    var dimBox = qs('[data-pw-dimensions]', root);
    var angBox = qs('[data-pw-angles]',     root);
    var matBox = qs('[data-pw-materials]',  root);
    var colBox = qs('[data-pw-colors]',     root);
    var qtyBox = qs('[data-pw-quantity]',   root);

    config.dimensions.forEach(function(d) {
      var row = document.createElement('div');
      row.className = 'pw-field pw-field--slider';
      var html =
        '<div class="pw-field__row">' +
          '<label class="pw-field__label" for="pw-' + d.id + '">' + d.label + '</label>' +
          '<span class="pw-field__val"><span id="pw-val-' + d.id + '">' + d.default + '</span>&nbsp;' + d.unit + '</span>' +
        '</div>' +
        '<input type="range" id="pw-' + d.id + '" class="pw-slider"' +
               ' min="' + d.min + '" max="' + d.max + '"' +
               ' step="' + d.step + '" value="' + d.default + '">';
      if (d.help) html += '<div class="pw-field__hint">' + d.help + '</div>';
      row.innerHTML = html;
      dimBox.appendChild(row);

      var slider  = row.querySelector('input[type=range]');
      var display = document.getElementById('pw-val-' + d.id);
      slider.addEventListener('input', function() {
        values[d.id] = Number(this.value);
        if (display) display.textContent = this.value;
        updateSliderFill(this);
        update();
      });
      updateSliderFill(slider);
    });

    if (angBox) {
      if (!config.angles || config.angles.length === 0) {
        var angSec = angBox.closest('.pw-calc__section');
        if (angSec) angSec.style.display = 'none';
      } else {
        config.angles.forEach(function(a) {
          var row = document.createElement('div');
          row.className = 'pw-field pw-field--inline';
          row.innerHTML =
            '<label class="pw-field__label" for="pw-' + a.id + '">' + a.label + '</label>' +
            '<div class="pw-field__control">' +
              '<input type="number" id="pw-' + a.id + '"' +
                     ' min="' + a.min + '" max="' + a.max + '" step="' + a.step + '"' +
                     ' value="' + a.default + '">' +
              '<span class="pw-field__unit">' + a.unit + '</span>' +
            '</div>';
          angBox.appendChild(row);
          row.querySelector('input').addEventListener('input', function() {
            values[a.id] = Number(this.value);
            update();
          });
        });
      }
    }

    config.materials.forEach(function(m) {
      var lbl = document.createElement('label');
      lbl.className = 'pw-material' + (m.id === values._material ? ' pw-material--active' : '');
      var extra = m.surcharge_sek > 0
        ? ' <span class="pw-material__surcharge">+' + m.surcharge_sek + ' kr/st</span>' : '';
      lbl.innerHTML =
        '<input type="radio" name="pw-material" value="' + m.id + '"' +
        (m.id === values._material ? ' checked' : '') + '>' +
        '<span>' + m.label + extra + '</span>';
      matBox.appendChild(lbl);
      lbl.querySelector('input').addEventListener('change', function() {
        values._material = m.id;
        qsa('.pw-material', matBox).forEach(function(n) { n.classList.remove('pw-material--active'); });
        lbl.classList.add('pw-material--active');
        update();
      });
    });

    var colorOpts = config.colors.slice();
    if (config.extended_ral && config.extended_ral.enabled) {
      colorOpts.push({
        ral: '_extended',
        label: config.extended_ral.label || 'Annan RAL-kul\xf6r',
        hex: '#b0b0b0',
        surcharge_sek: config.extended_ral.surcharge_sek,
        _extended: true
      });
    }
    colorOpts.forEach(function(c, i) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'pw-swatch' + (i === 0 ? ' pw-swatch--active' : '');
      if (c._extended) {
        btn.classList.add('pw-swatch--extended');
        btn.innerHTML = '<span aria-hidden="true">+</span>';
      } else {
        btn.style.background = c.hex;
        var h = c.hex.toLowerCase().replace('#','');
        if (h === 'f7f7f2' || h === 'ffffff' || h === 'fff') {
          btn.style.border = '1px solid #ccc';
        }
      }
      btn.setAttribute('aria-label', c._extended ? c.label : 'RAL ' + c.ral + ' ' + c.label);
      btn.setAttribute('title', c._extended ? c.label : 'RAL ' + c.ral + ' \u2014 ' + c.label);
      colBox.appendChild(btn);
      btn.addEventListener('click', function() {
        values._color_ral = c.ral;
        values._color_hex = c._extended ? '#474646' : c.hex;
        qsa('.pw-swatch', colBox).forEach(function(n) { n.classList.remove('pw-swatch--active'); });
        btn.classList.add('pw-swatch--active');
        var nameEl = qs('[data-pw-color-name]', root);
        if (nameEl) {
          nameEl.textContent = c._extended
            ? c.label + ' (+' + c.surcharge_sek + ' kr/st)'
            : 'RAL ' + c.ral + ' \u2014 ' + c.label;
        }
        update();
      });
    });
    var colName = qs('[data-pw-color-name]', root);
    if (colName) {
      var fc = config.colors[0];
      colName.textContent = 'RAL ' + fc.ral + ' \u2014 ' + fc.label;
    }

    if (qtyBox) {
      var qmin = config.quantity_min || 1;
      var qmax = config.quantity_max || 500;
      // V1.5 (2026-05-03): minimal stepper — bara [- input +] i en pill,
      // ingen intern label eller hint. Wrapper-rad styr layout. Hint för
      // staffelrabatt göms när enable_volume_discount=false.
      qtyBox.innerHTML =
        '<div class="pw-qty">' +
          '<button type="button" class="pw-qty__btn" data-pw-qty-minus aria-label="Minska">\u2212</button>' +
          '<input type="number" class="pw-qty__input" id="pw-qty" min="' + qmin + '" max="' + qmax + '" step="1" value="' + values._quantity + '">' +
          '<button type="button" class="pw-qty__btn" data-pw-qty-plus aria-label="\xd6ka">+</button>' +
        '</div>';
      var qIn     = qtyBox.querySelector('input');
      var minBtn  = qtyBox.querySelector('[data-pw-qty-minus]');
      var plusBtn = qtyBox.querySelector('[data-pw-qty-plus]');
      var syncQty = function() {
        values._quantity = Math.max(qmin, Math.min(qmax, Number(qIn.value) || 1));
        qIn.value = values._quantity;
        update();
      };
      qIn.addEventListener('input', syncQty);
      minBtn.addEventListener('click', function() { qIn.value = values._quantity - 1; syncQty(); });
      plusBtn.addEventListener('click', function() { qIn.value = values._quantity + 1; syncQty(); });
    }

    var svgRoot   = qs('[data-pw-svg]',        root);
    var p1El      = qs('[data-pw-unit-price]',  root);
    var p10El     = qs('[data-pw-bulk-price]',  root);
    var exVatEl   = qs('[data-pw-price-exvat]', root);
    var totalEl       = qs('[data-pw-total-price]',   root);
    var totalSumEl    = qs('[data-pw-total-summary]', root);
    var totalExVatEl  = qs('[data-pw-total-exvat]',   root);
    var discEl    = qs('[data-pw-discount]',    root);
    var qtyHintEl = qs('[data-pw-qty-hint]',    root);
    var addBtn    = qs('[data-pw-add]',         root);

    function update() {
      renderSvg(svgRoot, config, values);

      var br1  = calcUnitPrice(config, values, 1);
      var br10 = calcUnitPrice(config, values, 10);
      var brQ  = calcUnitPrice(config, values, values._quantity);

      if (p1El)  p1El.textContent  = formatSek(br1.unit_inc)  + ' kr';
      if (p10El) p10El.textContent = formatSek(br10.total_inc) + ' kr';

      if (exVatEl) {
        exVatEl.textContent =
          formatSek(brQ.total_inc) + ' kr totalt (inkl. moms) \u00b7 ' +
          formatSek(brQ.total_ex)  + ' kr exkl.';
      }

      // V1.5 — ny prismodul: stor total + qty-impact-summary
      if (totalEl) totalEl.textContent = formatSek(brQ.total_inc) + ' kr';
      if (totalSumEl) {
        var qty = brQ.qty || 1;
        var pieceWord = (qty === 1) ? 'styck' : 'styck';
        totalSumEl.textContent =
          'F\u00f6r ' + qty + ' ' + pieceWord +
          ' \u00b7 ' + formatSek(br1.unit_inc) + ' kr/st';
      }
      if (totalExVatEl) {
        totalExVatEl.textContent = formatSek(brQ.total_ex) + ' kr exkl. moms';
      }

      if (discEl) {
        if (PW_VOLUME_DISCOUNT_ENABLED && brQ.discount_pct > 0) {
          discEl.textContent = 'Staffelrabatt: \u221220% vid 10+ st';
          discEl.style.display = '';
        } else {
          discEl.style.display = 'none';
        }
      }

      if (qtyHintEl && PW_VOLUME_DISCOUNT_ENABLED) {
        var tiers = (config.quantity_discount && config.quantity_discount.tiers) || [];
        var next = null;
        tiers.forEach(function(t) {
          if (t.min_qty > brQ.qty && t.discount_pct > brQ.discount_pct) {
            if (!next) next = t;
          }
        });
        if (next) {
          qtyHintEl.textContent = 'Best\xe4ll ' + (next.min_qty - brQ.qty) + ' till f\xf6r \u2212' + next.discount_pct + '%';
        } else if (brQ.discount_pct > 0) {
          qtyHintEl.textContent = 'Staffelrabatt aktiv (\u2212' + brQ.discount_pct + '%)';
        } else {
          qtyHintEl.textContent = '10 st \u2192 \u221220% \u00b7 100 st \u2192 \u221225%';
        }
      } else if (qtyHintEl) {
        qtyHintEl.textContent = '';
      }

      if (addBtn) {
        addBtn.dataset.unitPrice  = brQ.unit_ex;
        addBtn.dataset.totalPrice = brQ.total_ex;
        addBtn.dataset.quantity   = brQ.qty;
      }
    }

    if (typeof ResizeObserver !== 'undefined') {
      var rendered = false;
      var ro = new ResizeObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.contentRect.width > 20) {
            update();
            if (!rendered) rendered = true;
          }
        });
      });
      ro.observe(svgRoot);
    }

    // ---- Lägg i varukorg --------------------------------------------------
    //
    // V1.5.0 (2026-04-22) — Cart Transformer-flöde. Kalkylatorn postar direkt
    // till /cart/add.js med basvariantens id + tekniska line item properties
    // (_pw_*). Shopifys Cart Transformer Function `pw-price-engine` läser
    // properties + produktens platwebb.profile_config-metafält och expanderar
    // raden till rätt pris. Ingen Edge Function i cart-flödet.
    //
    // dynamicAddToCart och legacyAddToCart är dead code från tidigare
    // arkitekturer — behålls i filen tills V1.5.0 är verifierad i produktion,
    // sedan städas bort.

    if (addBtn) {
      addBtn.addEventListener('click', function() {
        var brQ = calcUnitPrice(config, values, values._quantity);

        var orig = addBtn.textContent;
        addBtn.disabled = true;
        addBtn.textContent = 'L\xe4gger till\u2026';

        cartTransformAdd(config, values, brQ).catch(function(err) {
          console.error('[plat-kalkylator] cart-transform add failed', err);
          addBtn.disabled = false;
          addBtn.textContent = orig;
          alert('Kunde inte l\xe4gga i varukorgen \u2014 ladda om sidan och f\xf6rs\xf6k igen.');
        });
      });
    }

    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        update();
      });
    });
  }

  // ---- Cart-flow A: dynamic variant via Edge Function ---------------------

  function dynamicAddToCart(config, values, brQ) {
    var payload = buildVariantPayload(config, values, brQ);
    var canonical = normalizePayload(payload);

    return sha256Hex(canonical).then(function(hash) {
      payload.client_hash = hash;
      return fetchDynamicVariant(payload);
    }).then(function(resp) {
      // resp = { ok, variant_id, variant_gid, variant_sku, reused, line_item_properties }
      var props = resp.line_item_properties || {};
      // Komplettera med klient-sidans label-baserade props (mer mänskligt läsbart)
      enrichPropsFromClient(props, config, values, brQ);

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: resp.variant_id,
          quantity: brQ.qty,
          properties: props
        })
      }).then(function(res) {
        if (!res.ok) return res.text().then(function(t) { throw new Error(t); });
        window.location.href = '/cart';
      });
    });
  }

  function enrichPropsFromClient(props, config, values, brQ) {
    var mat = null;
    config.materials.forEach(function(m) { if (m.id === values._material) mat = m; });
    var colOpt = null;
    config.colors.forEach(function(c) { if (c.ral === values._color_ral) colOpt = c; });
    var extRal = values._color_ral === '_extended';

    // Mänskliga labels för dimensioner
    config.dimensions.forEach(function(d) {
      if (props[d.label] == null) props[d.label] = values[d.id] + ' ' + d.unit;
    });
    config.angles.forEach(function(a) {
      if (props[a.label] == null) props[a.label] = values[a.id] + a.unit;
    });

    if (!props['Material'] && mat) props['Material'] = mat.label;
    if (!props['Kul\xf6r']) {
      props['Kul\xf6r'] = extRal
        ? config.extended_ral.label + ' (+' + config.extended_ral.surcharge_sek + ' kr/st)'
        : 'RAL ' + (colOpt ? colOpt.ral : '') + ' ' + (colOpt ? colOpt.label : '');
    }
    props['_profile_id']      = config.profile_id;
    props['_unit_price_sek']  = String(brQ.unit_ex);
    props['_total_price_sek'] = String(brQ.total_ex);
    props['_discount_pct']    = String(brQ.discount_pct);
  }

  function humaneError(err) {
    if (!err) return 'Ok\xe4nt fel.';
    switch (err.code) {
      case 'PRICE_MISMATCH':
        return 'Priset st\xe4mmer inte. Ladda om sidan \u2014 priset kan ha uppdaterats.';
      case 'INVALID_HASH':
        return 'Tekniskt fel i konfigurationen. Ladda om sidan.';
      case 'PROFILE_NOT_FOUND':
        return 'Den h\xe4r produkttypen \xe4r inte tillg\xe4nglig just nu. Kontakta Pl\xe5twebb.';
      case 'VALIDATION_ERROR':
      case 'MISSING_FIELD':
        return 'Konfigurationen \xe4r inte komplett. Kontrollera dina v\xe4rden.';
      case 'SHOPIFY_ERROR':
      case 'CREDENTIALS_UNAVAILABLE':
        return 'V\xe5rt syst\xe4m svarar inte. F\xf6rs\xf6k igen om en stund.';
      default:
        return 'Kunde inte l\xe4gga i varukorgen \u2014 ladda om sidan och f\xf6rs\xf6k igen.';
    }
  }

  // ---- Cart-flow B: legacy direkt-add ------------------------------------

  function legacyAddToCart(config, values, brQ) {
    return configHashLegacy(config.profile_id, values, brQ).then(function(hash) {
      var mat = null;
      config.materials.forEach(function(m) { if (m.id === values._material) mat = m; });
      var colOpt = null;
      config.colors.forEach(function(c) { if (c.ral === values._color_ral) colOpt = c; });
      var extRal = values._color_ral === '_extended';

      var props = { 'L\xe4ngd': values.length + ' mm' };
      config.dimensions.forEach(function(d) {
        if (d.id !== 'length') props[d.label] = values[d.id] + ' ' + d.unit;
      });
      config.angles.forEach(function(a) { props[a.label] = values[a.id] + a.unit; });
      props['Material'] = mat ? mat.label : '';
      props['Kul\xf6r']  = extRal
        ? config.extended_ral.label + ' (+' + config.extended_ral.surcharge_sek + ' kr/st)'
        : 'RAL ' + (colOpt ? colOpt.ral : '') + ' ' + (colOpt ? colOpt.label : '');
      props['_profile_id']      = config.profile_id;
      props['_unit_price_sek']  = String(brQ.unit_ex);
      props['_total_price_sek'] = String(brQ.total_ex);
      props['_discount_pct']    = String(brQ.discount_pct);
      props['_config_hash']     = hash;

      var variantId = window.PLATWEBB_VARIANT_ID;
      if (!variantId) {
        return Promise.reject(new Error('PLATWEBB_VARIANT_ID saknas'));
      }

      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: brQ.qty, properties: props })
      }).then(function(res) {
        if (!res.ok) return res.text().then(function(t) { throw new Error(t); });
        window.location.href = '/cart';
      });
    });
  }

  function configHashLegacy(profileId, values, br) {
    var raw = JSON.stringify({ profileId: profileId, values: values,
                               unit: br.unit_ex, qty: br.qty });
    return sha256Hex(raw).then(function(full) { return full.slice(0, 16); });
  }

  // ---- Cart-flow (V1.5.0): Cart Transformer-flöde -----------------------
  //
  // Använder basvariantens variant_id + tekniska _pw_*-properties. Shopifys
  // Cart Transformer Function (pw-price-engine) läser properties och
  // expanderar line-item:et till rätt pris baserat på profile_config-metafält.
  //
  // Properties-kontrakt måste matcha cart_transform_run.graphql i
  // platwebb-discount-app/extensions/pw-price-engine.

  function cartTransformAdd(config, values, brQ) {
    var variantId = window.PLATWEBB_VARIANT_ID;
    if (!variantId) return Promise.reject(new Error('PLATWEBB_VARIANT_ID saknas'));

    var dims = {};
    config.dimensions.forEach(function(d) { dims[d.id] = Number(values[d.id]); });
    var angles = {};
    (config.angles || []).forEach(function(a) { angles[a.id] = Number(values[a.id]); });

    var mat = null;
    config.materials.forEach(function(m) { if (m.id === values._material) mat = m; });
    var colOpt = null;
    config.colors.forEach(function(c) { if (c.ral === values._color_ral) colOpt = c; });
    var extRal = values._color_ral === '_extended';

    // Kundsynlig sammanfattning av dimensioner — används i cart-drawer och på
    // orderkvitto. Prioritera "Längd" först om profilen har den.
    var dimParts = config.dimensions.map(function(d) {
      return d.label + ' ' + values[d.id] + ' ' + d.unit;
    });
    var dimSummary = dimParts.join(', ');

    var ralLabel = extRal
      ? (config.extended_ral ? config.extended_ral.label : 'Annan RAL')
      : 'RAL ' + (colOpt ? colOpt.ral : '') + ' ' + (colOpt ? colOpt.label : '');

    var props = {
      '_pw_profile':      config.profile_id,
      '_pw_material_id':  values._material,
      '_pw_ral':          extRal ? '' : (values._color_ral || ''),
      '_pw_ral_extended': extRal ? 'true' : 'false',
      '_pw_dims':         JSON.stringify(dims),
      '_pw_angles':       JSON.stringify(angles),
      '_pw_version':      'v2',
      // Kundsynliga — visas i cart och på orderkvitto
      'M\xe5tt':         dimSummary,
      'Material':         mat ? mat.label : '',
      'F\xe4rg':         ralLabel
    };

    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: brQ.qty, properties: props })
    }).then(function(res) {
      if (!res.ok) return res.text().then(function(t) { throw new Error(t); });
      window.location.href = '/cart';
    });
  }

  // ---- Slider fill-helper ------------------------------------------------
  function updateSliderFill(slider) {
    var min = Number(slider.min) || 0;
    var max = Number(slider.max) || 100;
    var val = Number(slider.value);
    var pct = ((val - min) / (max - min) * 100).toFixed(1);
    slider.style.setProperty('--pct', pct + '%');
  }

  // ---- Bootstrap ---------------------------------------------------------

  function init() {
    qsa('[data-pw-calculator]').forEach(function(root) {
      if (root.dataset.pwReady === '1') { return; }
      root.dataset.pwReady = '1';
      var url = root.dataset.pwCalculator;
      if (!url) { return; }
      fetch(url, { cache: 'no-cache' })
        .then(function(r) {
          if (!r.ok) throw new Error('Config ' + r.status);
          return r.json();
        })
        .then(function(config) { buildUI(root, config); })
        .catch(function(err) {
          console.error('[plat-kalkylator]', err);
          root.innerHTML = '<p class="pw-calc__error">Kalkylatorn kunde inte laddas. Ladda om eller kontakta Pl\xe5twebb.</p>';
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
